import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SALON_APP_BASE } from '../config/salonApp';
import {
  performSalonIdleLogout,
  hasActiveSalonLogin,
  recordSalonUserActivity,
  ensureSalonLoginValidated,
  shouldClearSalonSessionOnValidateFailure,
  clearSalonAuthStorage,
  clearSalonLoginServerValidated,
} from '../utils/salonAuth';

const PUBLIC_SALON_PATHS = new Set([SALON_APP_BASE, `${SALON_APP_BASE}/login`]);
const WATCH_INTERVAL_MS = 15000;
const REVALIDATE_INTERVAL_MS = 5 * 60 * 1000;

const ACTIVITY_EVENTS = ['click', 'touchstart', 'keydown'];

/**
 * Local idle timeout, periodic server revalidation, and logout redirect.
 */
export default function SalonAuthSessionWatcher() {
  const navigate = useNavigate();

  useEffect(() => {
    const onActivity = () => recordSalonUserActivity();

    ACTIVITY_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, onActivity, { capture: true, passive: true });
    });

    return () => {
      ACTIVITY_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, onActivity, { capture: true });
      });
    };
  }, []);

  useEffect(() => {
    const timer = window.setInterval(async () => {
      const path = window.location.pathname || '';
      if (PUBLIC_SALON_PATHS.has(path)) return;

      if (!hasActiveSalonLogin()) {
        await performSalonIdleLogout();
        navigate(`${SALON_APP_BASE}/login`, { replace: true });
      }
    }, WATCH_INTERVAL_MS);

    return () => window.clearInterval(timer);
  }, [navigate]);

  useEffect(() => {
    const timer = window.setInterval(async () => {
      const path = window.location.pathname || '';
      if (PUBLIC_SALON_PATHS.has(path)) return;
      if (!hasActiveSalonLogin()) return;

      clearSalonLoginServerValidated();
      const result = await ensureSalonLoginValidated({ force: true });
      if (result.ok) return;

      if (shouldClearSalonSessionOnValidateFailure(result)) {
        clearSalonAuthStorage();
        navigate(`${SALON_APP_BASE}/login`, { replace: true });
      }
    }, REVALIDATE_INTERVAL_MS);

    return () => window.clearInterval(timer);
  }, [navigate]);

  return null;
}
